import { useEffect, useMemo } from 'react';
import { useReloadAfterDataFlush } from 'hooks/useReloadAfterDataFlush';
import { useResetOnLogout } from 'store/entityCollection/useResetOnLogout';
import { subscribeSwConfigReady } from 'services/serviceWorker/swConfigReady';
import { EditableFieldValuesEntityName } from 'shared/domain/fieldValue/fields';
import { fieldValueEntityNames } from 'shared/domain/fieldValue/fieldValueClients';
import { useFieldValuesStore } from '../store';
import { toFieldValueDictionaries, toFieldValuesResult } from '../model';
import type { FieldValueDictionaries, FieldValuesResult } from '../types';

/** One dictionary of the selected project; loads it on first use. */
export function useFieldValues(entity: EditableFieldValuesEntityName): FieldValuesResult {
  const data = useFieldValuesStore((state) => state.data[entity]);
  const { ensureLoaded, reload } = useFieldValuesStore.use.actions();
  const generation = useFieldValuesStore.use.generation();

  // Again after a reset (`generation`), which drops the loaded dictionaries.
  useEffect(() => ensureLoaded(entity), [ensureLoaded, entity, generation]);

  return useMemo(
    () => ({ ...toFieldValuesResult(data), resync: () => void reload(entity) }),
    [data, entity, reload]
  );
}

/** Every dictionary of the selected project, for the forms; loads them on first use. */
export function useFieldValueDictionaries(): { dictionaries: FieldValueDictionaries; loading: boolean } {
  const data = useFieldValuesStore.use.data();
  const { ensureLoaded } = useFieldValuesStore.use.actions();
  const generation = useFieldValuesStore.use.generation();

  useEffect(() => {
    fieldValueEntityNames.forEach((entity) => ensureLoaded(entity));
  }, [ensureLoaded, generation]);

  return useMemo(() => toFieldValueDictionaries(data), [data]);
}

/**
 * Keeps loaded dictionaries in step with the worker: reload when its config is
 * ready again (also on resume) or its data was flushed, reset on logout and
 * when no project is selected. Mounted once, in WithEntityStoresInitializer.
 */
export function useFieldValuesLifecycle(): void {
  const { reloadLoaded, reset } = useFieldValuesStore.use.actions();

  useEffect(() => subscribeSwConfigReady(reloadLoaded), [reloadLoaded]);
  useEffect(() => reset, [reset]);
  useReloadAfterDataFlush(reloadLoaded);
  useResetOnLogout(reset);
}

export const useWorktypes = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.workTypes);
export const useHazardCategories = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.hazardCategory);
export const useEnvironmentalAspects = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.environmentalAspect);
export const useCorrectiveActionTypes = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.proposedCorrectiveAction);
export const useDelayReasons = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.delayReason);
export const useIssueVerificationItems = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.issueVerificationItem);
export const useRejectionReasons = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.rejectionReason);
export const useRootCauses = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.rootCauses);
export const useEffects = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.effect);
export const useCircumstances = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.circumstances);
export const useImpacts = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.impact);
export const useDecisionsToImposeFine = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.decisionToImposeFine);
export const useSpilledSubstances = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.spilledSubstance);
export const usePriorities = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.priority);
export const useProjectStages = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.projectStage);
export const useEmploymentTypes = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.employmentType);
export const useInjuryTypes = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.injuryType);
export const useInjuredBodyParts = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.injuredBodyPart);
export const useRequirements = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.requirements);
export const useInstallationBasis = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.installationBasis);
export const useConstructionProductDocuments = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.constructionProductDocuments);
export const useDeviceDocuments = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.deviceDocuments);
export const useAdditionalDocuments = (): FieldValuesResult => useFieldValues(EditableFieldValuesEntityName.additionalDocuments);
